import React, { useCallback, useEffect, useState } from "react";
import api from "../utils/base_url";
import toast from "react-hot-toast";
import "../styles/ReviewBox.css";

const ReviewBox = ({ productId }) => {
    const [summary, setSummary] = useState(null);   // left side summary
    const [reviews, setReviews] = useState([]);     // right side reviews
    const [activeStar, setActiveStar] = useState(null); // filter star
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);

    const fetchSummary = useCallback(async () => {
        try {
            const res = await api.get(`/product-rating-summary/${productId}/`);
            setSummary(res.data);
        } catch (error) {
            console.error(error);
            toast.error("Failed to load rating summary");
        }
    }, [productId]);


    const fetchReviews = useCallback(
        async (pageNum = 1, starFilter = activeStar) => {
            try {
                setLoading(true);

                const url = `/product-feedback-filter/${productId}/?page=${pageNum}${starFilter ? `&rating=${starFilter}` : ""
                    }`;

                const res = await api.get(url);
                const db = res.data.results;

                if (pageNum === 1) setReviews(db);
                else setReviews((prev) => [...prev, ...db]);

                setHasMore(res.data.next !== null);
                setLoading(false);
            } catch (error) {
                console.error(error);
                toast.error("Failed to load reviews");
                setLoading(false);
            }
        },
        [productId, activeStar] // correct deps
    );

    // On component load
    useEffect(() => {
        fetchSummary();
        fetchReviews(1);
    }, [fetchSummary, fetchReviews]);


    // On filter click
    const handleFilter = (star) => {
        setActiveStar(star);
        setPage(1);
        fetchReviews(1, star);
    };

    // Load next page
    const loadMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchReviews(nextPage);
    };

    if (!summary) return <p>Loading reviews…</p>;

    return (
        <div className="review-container">
            {/* LEFT: SUMMARY */}
            <div className="review-left">
                <h3 className="title">Customer Reviews</h3>
                <div className="rating-summary-box">
                    <div className="rating-top">
                        <span className="rating-value">{summary.average}</span>
                        <span className="rating-value">out of 5</span>
                    </div>

                    <div className="rating-stars">
                        {"★".repeat(Math.round(summary.average))}
                        {"☆".repeat(5 - Math.round(summary.average))}
                    </div>

                    <div className="rating-total">{summary.total} global ratings</div>
                </div>


                {/* STAR PERCENTAGE BARS */}
                <div className="star-bars">
                    {[5, 4, 3, 2, 1].map((star) => (
                        <div className="bar-row" key={star}>
                            <span className="star-label">{star} ★</span>
                            <div className="bar-track">
                                <div
                                    className="bar-fill"
                                    style={{ width: `${summary.percentage[star]}%` }}
                                ></div>
                            </div>
                            <span className="bar-count">{summary.counts[star]}</span>
                        </div>
                    ))}
                </div>

                {/* FILTER BUTTONS */}
                <div className="filter-buttons">
                    <button
                        className={!activeStar ? "active" : ""}
                        onClick={() => handleFilter(null)}
                    >
                        All Reviews
                    </button>

                    {[5, 4, 3, 2, 1].map((star) => (
                        <button
                            key={star}
                            className={activeStar === star ? "active" : ""}
                            onClick={() => handleFilter(star)}
                        >
                            {star} ★
                        </button>
                    ))}
                </div>
            </div>

            {/* RIGHT: REVIEWS LIST */}
            <div className="review-right">
                {reviews.length === 0 ? (
                    <div className="no-review-green">
                        <i className="fa fa-comments"></i>
                        <p>No reviews yet</p>
                        <small>Be the first to share your experience</small>
                    </div>

                ) : (
                    reviews.map((rev) => (
                        <div className="review-card" key={rev.id}>
                            <div className="review-stars">
                                {"★".repeat(rev.rating)}
                                {"☆".repeat(5 - rev.rating)}
                            </div>
                            <div className="review-user">{rev.user_name}</div>
                            <div className="review-comment">{rev.comment}</div>
                            <div className="review-date">
                                {new Date(rev.created_at).toLocaleDateString()}
                            </div>
                        </div>
                    ))
                )}

                {/* Load More */}
                {hasMore && !loading && (
                    <div className="load-more-container">
                        <button className="load-more" onClick={loadMore}>
                            Load More
                        </button>
                    </div>

                )}

                {loading && <p>Loading...</p>}
            </div>
        </div>
    );
};

export default ReviewBox;
